<template>
	<div
		class="flex flex-col w-full relative items-start group"
		:class="contentStyles || ''"
	>
		<div class="w-full relative">
			<ProductCardImage
				:product="product"
				:picture-size="pictureSize"
				:picture-aspect-ratio="pictureAspectRatio"
				:click-is-valid="clickIsValid"
				:lazy-loading="lazyLoading"
				:link-styles="linkStyles"
				@click="handleClick"
			>
				<ProductCardActions
					v-if="showVariants"
					:product="product"
					:variants="variants"
					:all-parametrics="allParametrics"
					:show-variants="showVariants"
					@open-modal="openQuickBuyModal"
				/>
			</ProductCardImage>
			<NuxtLink
				v-if="product"
				class="cursor-pointer w-full"
				:to="clickIsValid ? routeToProduct : ''"
				@click="handleClick"
			>
				<div class="flex flex-col mt-4 gap-2 items-start">
					<span class="font-medium text-base text-left">{{
						product.Name
					}}</span>
					<Price
						:price="product.PriceIncVat!"
						:old-price="oldPrice"
						:show-save-text="false"
					/>
					<RatingProduct :rating="{ score: 5, votes: 4 }" />
				</div>
			</NuxtLink>
		</div>
		<ProductVariants
			v-if="variants?.length > 0 && allParametrics"
			:variants="variants"
			:all-parametrics="allParametrics"
		/>
		<QuickBuyModal
			v-if="isQuickBuyModal"
			:product="product as FilteredProductItem"
			:variants="variants || []"
			:all-parametrics="allParametrics || []"
			:is-open="isQuickBuyModalOpen"
			@close="closeQuickBuyModal"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	ProductItem,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import Price from "./ProductAttributes/Price.vue";
import ProductVariants from "./ProductVariants.vue";
import ProductCardImage from "./ProductCardImage.vue";
import ProductCardActions from "./ProductCardActions.vue";
import QuickBuyModal from "./QuickBuyModal.vue";
import { useGtmEvents } from "~/composables/useGtmEvents";
import { useProductQuickBuy } from "~/composables/useProductQuickBuy";

interface Props {
	product: Partial<FilteredProductItem> | ProductItem;
	pictureSize: string;
	pictureAspectRatio?: string;
	clickIsValid?: boolean;
	lazyLoading: boolean | undefined;
	contentStyles?: string;
	linkStyles?: string;
	variants?: FilteredProductItem[];
	allParametrics?: ParametricListValue[];
	showVariants?: boolean;
}
const {
	product,
	pictureAspectRatio,
	pictureSize,
	lazyLoading,
	clickIsValid = true,
	variants,
	allParametrics,
	showVariants = false
} = defineProps<Props>();
const emit = defineEmits(["product-click"]);

const { productClick } = useGtmEvents();
const { oldPrice } = useProductPrices(product);

const routeToProduct = computed(() => {
	return `/product/${product?.UniqueName}`;
});

const handleClick = () => {
	if (clickIsValid) {
		productClick(product);
		emit("product-click");
	}
};

const isQuickBuyModalOpen = ref(false);

const { isModal } = useProductQuickBuy({
	product,
	variants,
	allParametrics,
	showVariants
});

const isQuickBuyModal = computed(() => {
	return isModal.value && product && "PartNo" in product && showVariants;
});

const openQuickBuyModal = () => {
	isQuickBuyModalOpen.value = true;
};

const closeQuickBuyModal = () => {
	isQuickBuyModalOpen.value = false;
};
</script>
